import type {
  IncomeRoll,
  InputSpec,
  RoundView,
  Standing,
  Submission,
} from '@guess/engine';

/**
 * Le contrat entre client et serveur.
 *
 * Deux regles non negociables :
 *   - le serveur detient l'horloge ET les reponses ; le client n'a jamais de
 *     quoi calculer un score ni deviner une solution ;
 *   - rien ici ne transporte `Question.answer`. `RoundView` est deja construit
 *     pour ca, on le relaie tel quel.
 */

export const PROTOCOL_VERSION = 1;

/** Etat general d'un salon. */
export type Phase = 'lobby' | 'loadout' | 'betting' | 'playing' | 'result' | 'over';

/**
 * Les avatars connus. Une liste FERMEE, cote serveur, plutot qu'une chaine
 * libre : accepter n'importe quel texte transformerait ce champ en canal de
 * message deguise. Le client tient exactement la meme liste pour son propre
 * choix ; `undefined` cote client retombe sur un avatar par defaut derive du
 * siege, jamais sur du texte libre affiche tel quel.
 */
export const AVATAR_IDS = [
  'bolt',
  'ghost',
  'diamond',
  'star',
  'hex',
  'ring',
  'triangle',
  'cross',
] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

/** Une des huit teintes de siege (`--c-seat-1` a `--c-seat-8`), 1-indexee. */
export type ColorId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface PlayerInfo {
  id: string;
  name: string;
  /** true pour un bot : le client les affiche differemment. */
  bot: boolean;
  connected: boolean;
  bankroll: number;
  /** Choisi dans le salon. Absent tant que le joueur n'a rien choisi. */
  avatarId?: AvatarId;
  /** Idem. Absent = couleur deduite du rang au siege, comme avant. */
  colorId?: ColorId;
}

export interface PowerCard {
  id: string;
  label: string;
  description: string;
  kind: 'bonus' | 'malus';
  targeting: 'self' | 'other';
  charges: number | null;
  cooldownRounds: number;
}

/** Etat d'un pouvoir pour le joueur destinataire du message. */
export interface PowerSlot {
  id: string;
  label: string;
  targeting: 'self' | 'other';
  usesLeft: number | null;
  cooldownLeft: number;
  usable: boolean;
}

/** Ce qu'on annonce d'une question AVANT de la montrer : juste de quoi miser. */
export interface QuestionTeaser {
  miniGame: string;
  theme: string;
  prompt: string;
  inputKind: InputSpec['kind'];
}

/** Un theme du catalogue, avec le nombre de questions qu'il contient. */
export interface ThemeInfo {
  id: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Client -> serveur
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { t: 'join'; room: string; name: string }
  /** Reprend un siege deja occupe : rechargement de page, coupure reseau. */
  | { t: 'rejoin'; room: string; playerId: string }
  /**
   * Quitte la table pour de bon. Distinct d'une simple coupure de socket, qui
   * garde le siege pour un `rejoin` : ici, le siege est rendu et ne se
   * reprend pas.
   */
  | { t: 'leave' }
  /** Le proprietaire du salon ajoute un bot pour completer la table. */
  | { t: 'addBot'; level?: 'facile' | 'normal' | 'fort' }
  | { t: 'removeBot'; playerId: string }
  | { t: 'loadout'; powers: string[] }
  /** Choisi dans le salon, avant la partie. L'un ou l'autre, ou les deux. */
  | { t: 'customize'; avatarId?: AvatarId; colorId?: ColorId }
  /**
   * Reglages de partie, avant le lancement. Hote seulement, salon seulement.
   * Chaque champ omis laisse le reglage correspondant inchange.
   */
  | { t: 'configure'; themes?: string[]; rounds?: number; powersEnabled?: boolean }
  | { t: 'ready'; ready: boolean }
  | { t: 'stake'; amount: number }
  | { t: 'power'; powerId: string; targetId?: string }
  /** Demande l'exclusivite de reponse, sur une manche a buzzer. */
  | { t: 'buzz' }
  | { t: 'answer'; submission: Submission }
  | { t: 'ping' };

// ---------------------------------------------------------------------------
// Serveur -> client
// ---------------------------------------------------------------------------

export interface LobbyState {
  phase: Phase;
  room: string;
  /** Le joueur a qui ce message est adresse. */
  you: string;
  hostId: string;
  players: PlayerInfo[];
  readyIds: string[];
  roundNumber: number;
  totalRounds: number;
  loadoutSize: number;
  catalogue: PowerCard[];
  /** Tous les themes du catalogue de base, pour construire les filtres. */
  availableThemes: ThemeInfo[];
  /** Vide = aucun filtre, tous les themes sont joues. */
  selectedThemes: string[];
  /** Faux si l'hote a coupe les pouvoirs (bonus/malus) pour cette partie. */
  powersEnabled: boolean;
}

export interface BettingState {
  roundNumber: number;
  totalRounds: number;
  teaser: QuestionTeaser;
  /** Bornes de mise propres au destinataire. */
  range: { min: number; max: number };
  bankroll: number;
  /** Mise actuellement engagee (le minimum tant qu'on n'a rien choisi). */
  stake: number;
  powers: PowerSlot[];
  /** Pouvoirs deja poses par tout le monde, cible comprise. */
  declared: { ownerId: string; powerId: string; targetId: string }[];
  standings: Standing[];
  /** Temps restant pour miser. */
  remainingMs: number;
}

export type ServerMessage =
  | { t: 'hello'; version: number }
  | { t: 'joined'; playerId: string; room: string }
  | { t: 'lobby'; state: LobbyState }
  /** Les jets de des de la manche qui commence. */
  | { t: 'income'; rolls: IncomeRoll[] }
  | { t: 'betting'; state: BettingState }
  /** Vue de manche, poussee au changement de palier et a chaque action. */
  | { t: 'round'; view: RoundView }
  /** Retour prive sur sa propre reponse. */
  | { t: 'answered'; correct: boolean; delta: number; detail?: string }
  | {
      t: 'result';
      answer: string;
      subtitle?: string;
      /** Image nette (pas la version pixelisee/floutee de la manche), pour un
       *  recap propre — voir `Question.reveal.imageUrl`. */
      imageUrl?: string;
      notes: string[];
      standings: Standing[];
      bailedOut: string[];
      remainingMs: number;
    }
  | { t: 'over'; standings: Standing[] }
  | { t: 'error'; message: string };

/** Parse defensif : tout ce qui arrive du reseau est suspect. */
export function parseClientMessage(raw: string): ClientMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const msg = value as { t?: unknown };
  if (typeof msg.t !== 'string') return null;

  // Le detail de chaque variante est valide par le salon, qui seul connait le
  // contexte (phase, bornes de mise, pouvoirs disponibles).
  return value as ClientMessage;
}
